// EN mirror of /privacy — Privacy Policy (PDPA + GDPR-aligned)
import { RelatedHubGrid } from "@/components/RelatedHubGrid";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ShieldCheck } from "lucide-react";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { BRAND_URL } from "@/lib/brand";

const TITLE = "Privacy Policy — PDPA & GDPR Compliance | NYC Language Institute";
const DESC =
  "How NYC Language Institute collects, uses, retains, and safeguards personal data under Thailand's PDPA (พ.ร.บ.คุ้มครองข้อมูลส่วนบุคคล พ.ศ — ทีมงานตอบไวทุกวัน ปรึกษาก่อนตัดสินใจได้ฟรี";

export const Route = createFileRoute("/en/privacy")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/en/privacy", "en")},
      { name: "description", content: ctaDesc(DESC, "/en/privacy", "en")},
      { property: "og:title", content: ctaTitle(TITLE, "/en/privacy", "en")},
      { property: "og:description", content: ctaDesc(DESC, "/en/privacy", "en")},
      { property: "og:url", content: `${BRAND_URL}/en/privacy` },
      { property: "og:locale", content: "en_US" },
      { name: "robots", content: "index,follow" },
    ],
    links: [
      { rel: "canonical", href: `${BRAND_URL}/en/privacy` },
      ...hreflangLinks("/privacy", "/en/privacy"),
    ],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: TITLE, url: `${BRAND_URL}/en/privacy`, inLanguage: "en",
        about: { "@type": "Thing", name: "Personal Data Protection Act B.E. 2562" },
      }),
    }],
  }),
  component: EnPrivacy,
});

const SECTIONS = [
  { h: "1. Data controller", p: "NYC Language Institute Co., Ltd., 92/25 Sathon Nuea Rd, Silom, Bang Rak, Bangkok 10500 acts as the data controller for all information you submit through this website, LINE @nycli, email, or physical intake." },
  { h: "2. Lawful basis", p: "We process personal data on four bases: (a) performance of a translation / legalization contract you request; (b) our legitimate interest in fraud prevention and service-quality auditing; (c) legal obligation (KYC for notarial acts, accounting retention under Thai Revenue Code s.87); (d) your explicit consent for marketing." },
  { h: "3. Categories of data", p: "Identity data (name, passport / Thai ID number), contact data (email, phone, LINE ID), document content (birth certificate, marriage certificate, criminal record — treated as sensitive personal data), payment metadata (bank slip reference — we never store card PANs), and technical logs (IP, user-agent, page path)." },
  { h: "4. Retention", p: "Translation project files: 7 years after delivery (per Thai Revenue audit window). Notarial records: 10 years (Lawyers Council rule). Marketing lists: until you unsubscribe. Server access logs: 180 days rolling. Sensitive document scans are purged from working folders within 30 days of collection and moved to encrypted cold storage." },
  { h: "5. Recipients", p: "We share data only with (a) Thai MFA Legalization Division when apostille is requested, (b) destination embassy when embassy legalization is part of your order, (c) certified translators bound by NDA, (d) payment processors (SCB, Kbank), (e) cloud infrastructure (Cloudflare, Supabase EU region). We do not sell data, ever." },
  { h: "6. Your rights (PDPA s.30–36 / GDPR Art. 15–22)", p: "You may request access, correction, erasure, restriction, portability, and objection at dpo@nyclanguageinstitute.com. Response within 30 days. You may also lodge a complaint with the Thai Personal Data Protection Committee (PDPC) at pdpc.or.th." },
  { h: "7. Cross-border transfers", p: "When a translation is delivered to an overseas embassy or apostille destination (e.g., Germany, Australia), the recipient country's data-protection regime applies to that copy. We rely on adequacy decisions (EU, UK, JP, KR) or Standard Contractual Clauses (US, other)." },
  { h: "8. Security", p: "TLS 1.3 in transit; AES-256 at rest; role-based access on production databases with quarterly audit; MFA required for all staff logins; no personal data on removable media; physical documents in a fireproof safe until pickup." },
  { h: "9. Cookies", p: "Strictly necessary cookies only by default. Analytics and marketing pixels load only after you accept via the cookie banner. See the cookie table on /en/legal/cookies for the full inventory." },
  { h: "10. Changes", p: "We publish material changes 30 days before they take effect. The current version is 2026-07-16." },
];

function EnPrivacy() {
  return (
    <main className="min-h-screen bg-white">
      <section className="bg-navy-deep px-4 py-14 text-white">
        <div className="mx-auto max-w-4xl">
          <Link to="/en" className="text-xs text-amber-300 hover:underline">← EN home</Link>
          <div className="mt-3 flex items-center gap-3">
            <ShieldCheck className="h-8 w-8 text-amber-300" aria-hidden />
            <h1 className="text-3xl font-extrabold sm:text-4xl">Privacy Policy</h1>
          </div>
          <p className="mt-3 max-w-2xl text-white/80">
            Effective 16 July 2026 · PDPA B.E. 2562 · GDPR-aligned · replaces all prior versions.
          </p>
        </div>
      </section>
      <section className="px-4 py-12">
        <div className="mx-auto max-w-3xl space-y-6">
          {SECTIONS.map((s) => (
            <article key={s.h} className="rounded-2xl border border-slate-200 bg-white p-5">
              <h2 className="text-lg font-bold text-navy-deep">{s.h}</h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-700">{s.p}</p>
            </article>
          ))}
          <p className="text-xs text-slate-500">
            Data-subject requests: <a className="text-amber-700 underline" href="mailto:dpo@nyclanguageinstitute.com">dpo@nyclanguageinstitute.com</a> · Thai version: <Link to="/privacy" className="text-amber-700 underline">/privacy</Link>
          </p>
        </div>
      </section>
    <RelatedHubGrid locale="en" /></main>
  );
}
